import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import LiveSupportService from 'services/LiveSupportService';

// Async thunks
export const getSupportChats = createAsyncThunk(
    'liveSupport/data/getSupportChats',
    async (params = {}) => {
        try {
            const chats = await LiveSupportService.getSupportChats();
            return {
                chats,
                total: chats.length
            };
        } catch (error) {
            console.log('Error fetching support chats:', error);
            throw error;
        }
    }
);

export const getSupportChatDetails = createAsyncThunk(
    'liveSupport/data/getSupportChatDetails',
    async (chatId) => {
        try {
            const chatDetails = await LiveSupportService.getSupportChatDetails(chatId);
            return chatDetails;
        } catch (error) {
            console.log('Error fetching support chat details:', error);
            throw error;
        }
    }
);

export const createSupportChat = createAsyncThunk(
    'liveSupport/data/createSupportChat',
    async ({ userId, userInfo }) => {
        try {
            const chatData = await LiveSupportService.getOrCreateSupportChat(userId, userInfo);
            return chatData;
        } catch (error) {
            console.log('Error creating support chat:', error);
            throw error;
        }
    }
);

export const sendSupportMessage = createAsyncThunk(
    'liveSupport/data/sendSupportMessage',
    async ({ chatId, senderId, message, type = 'text' }) => {
        try {
            const result = await LiveSupportService.sendMessage(chatId, senderId, message, type);
            return result;
        } catch (error) {
            console.log('Error sending support message:', error);
            throw error;
        }
    }
);

export const markSupportChatAsRead = createAsyncThunk(
    'liveSupport/data/markSupportChatAsRead',
    async ({ chatId, userId }) => {
        try {
            await LiveSupportService.markAsRead(chatId, userId);
            return { chatId, userId };
        } catch (error) {
            console.log('Error marking support chat as read:', error);
            throw error;
        }
    }
);

// Real-time chat updates action
export const updateSupportChats = createAction('liveSupport/data/updateSupportChats');

export const getSupportUser = createAsyncThunk(
    'liveSupport/data/getSupportUser',
    async () => {
        try {
            const supportUser = await LiveSupportService.getSupportUser();
            return supportUser;
        } catch (error) {
            console.log('Error fetching support user:', error);
            throw error;
        }
    }
);

// Initial state
const initialState = {
    loading: false,
    error: null,
    chats: [],
    selectedChat: null,
    chatDetails: null,
    messages: [],
    supportUser: null,
    total: 0,
    stats: {
        total: 0,
        active: 0,
        unread: 0
    }
};

// Slice
const dataSlice = createSlice({
    name: 'liveSupport/data',
    initialState,
    reducers: {
        setSelectedChat: (state, action) => {
            state.selectedChat = action.payload;
        },
        setMessages: (state, action) => {
            // Ensure no duplicate messages when setting all messages
            const newMessages = action.payload || [];
            const messageMap = new Map();

            // Deduplicate messages by ID, keeping the latest version
            newMessages.forEach(msg => {
                if (msg.id && (!messageMap.has(msg.id) || msg.timestamp > messageMap.get(msg.id).timestamp)) {
                    messageMap.set(msg.id, msg);
                }
            });

            state.messages = Array.from(messageMap.values()).sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
        },
        addMessage: (state, action) => {
            const newMessage = action.payload;
            if (!newMessage.id) return;

            // Check if message already exists
            const existingIndex = state.messages.findIndex(msg => msg.id === newMessage.id);
            if (existingIndex >= 0) {
                // Update existing message if new one has later timestamp
                if (newMessage.timestamp > state.messages[existingIndex].timestamp) {
                    state.messages[existingIndex] = newMessage;
                }
            } else {
                // Add new message and maintain sort order
                state.messages.push(newMessage);
                state.messages.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
            }
        },
        updateChatLastMessage: (state, action) => {
            const { chatId, message, timestamp, senderId } = action.payload;
            const chat = state.chats.find(c => c.id === chatId);
            if (chat) {
                chat.lastMessage = message;
                chat.lastMessageTimestamp = timestamp;
                chat.lastMessageSenderId = senderId;
                chat.lastUpdated = timestamp;
            }
        },
        updateChatUnreadStatus: (state, action) => {
            const { chatId, userId, unread } = action.payload;
            const chat = state.chats.find(c => c.id === chatId);
            if (chat && chat.participantsInfo && chat.participantsInfo[userId]) {
                chat.participantsInfo[userId].unread = unread;
            }
        },
        updateUserPresence: (state, action) => {
            const { chatId, userId, isOnline, lastSeen } = action.payload;
            const chat = state.chats.find(c => c.id === chatId);
            if (chat && chat.participantsInfo && chat.participantsInfo[userId]) {
                chat.participantsInfo[userId].isOnline = isOnline;
                chat.participantsInfo[userId].lastSeen = lastSeen;
            }
        },
        clearSelectedChat: (state) => {
            state.selectedChat = null;
            state.chatDetails = null;
            state.messages = [];
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Get support chats
            .addCase(getSupportChats.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getSupportChats.fulfilled, (state, action) => {
                state.loading = false;
                state.chats = action.payload.chats;
                state.total = action.payload.total;

                // Calculate stats
                state.stats.total = action.payload.total;
                state.stats.active = action.payload.chats.filter(chat =>
                    chat.isActive === true
                ).length;
                // Count unread chats where support user has unread messages
                state.stats.unread = action.payload.chats.filter(chat => {
                    const supportInfo = chat.participantsInfo?.['support'];
                    return supportInfo?.unread === true;
                }).length;
                state.stats.online = action.payload.chats.filter(chat => {
                    const userInfo = Object.values(chat.participantsInfo || {}).find(p => p.role === 'user');
                    return userInfo?.isOnline === true;
                }).length;
            })
            .addCase(getSupportChats.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })

            // Get support chat details
            .addCase(getSupportChatDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getSupportChatDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.chatDetails = action.payload;
            })
            .addCase(getSupportChatDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })

            // Create support chat
            .addCase(createSupportChat.fulfilled, (state, action) => {
                const newChat = action.payload;
                const existingIndex = state.chats.findIndex(chat => chat.id === newChat.chatId);

                if (existingIndex >= 0) {
                    state.chats[existingIndex] = { ...state.chats[existingIndex], ...newChat };
                } else {
                    state.chats.unshift(newChat);
                }

                state.total = state.chats.length;
            })

            // Send support message
            .addCase(sendSupportMessage.fulfilled, (state, action) => {
                if (action.payload.success) {
                    state.messages.push(action.payload.data);
                }
            })

            // Mark support chat as read
            .addCase(markSupportChatAsRead.fulfilled, (state, action) => {
                const { chatId, userId } = action.payload;
                const chat = state.chats.find(c => c.id === chatId);
                if (chat && chat.participantsInfo && chat.participantsInfo[userId]) {
                    chat.participantsInfo[userId].unread = false;
                }
            })

            // Get support user
            .addCase(getSupportUser.fulfilled, (state, action) => {
                state.supportUser = action.payload;
            })

            // Real-time chat updates
            .addCase(updateSupportChats, (state, action) => {
                state.chats = action.payload.chats;
                state.total = action.payload.total;

                // Calculate stats
                state.stats.total = action.payload.total;
                state.stats.active = action.payload.chats.filter(chat =>
                    chat.isActive === true
                ).length;
                // Count unread chats where support user has unread messages
                state.stats.unread = action.payload.chats.filter(chat => {
                    const supportInfo = chat.participantsInfo?.['support'];
                    return supportInfo?.unread === true;
                }).length;
                state.stats.online = action.payload.chats.filter(chat => {
                    const userInfo = Object.values(chat.participantsInfo || {}).find(p => p.role === 'user');
                    return userInfo?.isOnline === true;
                }).length;
            });
    }
});

export const {
    setSelectedChat,
    setMessages,
    addMessage,
    updateChatLastMessage,
    updateChatUnreadStatus,
    updateUserPresence,
    clearSelectedChat,
    clearError
} = dataSlice.actions;

export default dataSlice.reducer;
